import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { adminApi, PerUserUsageReport, PerUserUsageRow } from '../services/api';
import PageHelp from '../components/PageHelp';
import { PAGE_HELP } from '../components/page-help-content';
import { getCurrentPeriod, getRecentPeriods, formatPeriodLabel } from '../utils/period';
import { ChipColor, getStatusMeta, POOL_LABELS } from '../utils/usage-status';
import { formatNum, formatPercent } from '../utils/format';

const ROWS_PER_PAGE = 25;

// Per-user context: a shared-pool member has no PERSONAL cap (they draw from the
// org pool), so 'unknown' reads "No personal cap" rather than the per-org
// "No allocation".
const STATUS_META = getStatusMeta('No personal cap');

function userDisplayName(row: PerUserUsageRow): string {
  const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  return name || row.email || row.userId;
}

const PerUserUsagePage: React.FC = () => {
  const [period, setPeriod] = useState<string>(getCurrentPeriod());
  const [report, setReport] = useState<PerUserUsageReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);

  const periods = useMemo(() => getRecentPeriods(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getPerUserUsage({ period })
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load per-user usage.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load per-user usage.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const summary = report?.summary;

  // Client-side pagination over the (already top-N-capped) ranked rows. Clamp the
  // page in render so a period change never lands on a blank page.
  const allRows = report?.users ?? [];
  const maxPage = Math.max(0, Math.ceil(allRows.length / ROWS_PER_PAGE) - 1);
  const safePage = Math.min(page, maxPage);
  const pagedRows = allRows.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);

  // When the whole stack is shared-pool (no per-seat tiers), every user's
  // Allocated/Remaining/Consumed is null — hide those columns so the table
  // isn't a wall of dashes. They reappear automatically if any user has a
  // personal cap (per-seat orgs).
  const anyPersonalCap = allRows.some((u) => u.runsAllocated !== null);

  const cards: { key: string; caption: string; value: string }[] = [
    { key: 'total', caption: 'Total users', value: (summary?.totalUsers ?? 0).toLocaleString() },
    { key: 'active', caption: 'Active this month', value: (summary?.activeUsers ?? 0).toLocaleString() },
    { key: 'runs', caption: 'Total runs', value: (summary?.totalRuns ?? 0).toLocaleString() },
    { key: 'avg', caption: 'Avg runs / user', value: (summary?.averageRunsPerUser ?? 0).toLocaleString() },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Per-User Usage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Per-user run consumption for the selected month, ranked champions-first — the power users
            driving adoption inside each brokerage. Usage is metered in runs (~200k tokens each).
          </Typography>
        </Box>
        <TextField
          select
          label="Period"
          size="small"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          sx={{ minWidth: 200 }}
          inputProps={{ 'data-testid': 'period-select' }}
        >
          {periods.map((p) => (
            <MenuItem key={p} value={p}>
              {formatPeriodLabel(p)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <PageHelp {...PAGE_HELP.perUserUsage} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && report && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
            data-testid="summary-cards"
          >
            {cards.map((card) => (
              <Card key={card.key} sx={{ flex: 1 }} data-testid={`card-${card.key}`}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {card.caption}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 700, mt: 1 }}
                    data-testid={`card-${card.key}-value`}
                  >
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" data-testid="user-table">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Used</TableCell>
                    {anyPersonalCap && (
                      <>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Allocated</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Remaining</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Consumed</TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={anyPersonalCap ? 8 : 5} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No users on this stack for this period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pagedRows.map((row, localIndex) => {
                    // Global rank across pages — the champion is the #1 overall, not page-relative.
                    const index = safePage * ROWS_PER_PAGE + localIndex;
                    const meta = STATUS_META[row.status] ?? STATUS_META.unknown;
                    const barValue =
                      row.percentConsumed === null ? 0 : Math.min(row.percentConsumed, 100);
                    const barColor: ChipColor = meta.color === 'default' ? 'warning' : meta.color;
                    // The #1 user with any runs is the month's "champion" (Marcus's framing).
                    const isChampion = index === 0 && row.runsUsed > 0;
                    return (
                      <TableRow key={row.userId} hover data-testid={`row-${row.userId}`}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {userDisplayName(row)}
                            </Typography>
                            {isChampion && (
                              <Tooltip title="Top run-volume user this month">
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  icon={<EmojiEventsIcon />}
                                  label="Champion"
                                  data-testid={`champion-${row.userId}`}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                          {row.email && userDisplayName(row) !== row.email && (
                            <Typography variant="caption" color="text.secondary">
                              {row.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.organizationName || row.organizationId || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.tierName || POOL_LABELS[row.poolModel]}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                          {row.runsUsed.toLocaleString()}
                        </TableCell>
                        {anyPersonalCap && (
                          <>
                            <TableCell sx={{ textAlign: 'right' }}>{formatNum(row.runsAllocated)}</TableCell>
                            <TableCell sx={{ textAlign: 'right' }}>{formatNum(row.runsRemaining)}</TableCell>
                            <TableCell>
                              {row.percentConsumed === null ? (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              ) : (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={barValue}
                                    color={barColor}
                                    sx={{ flex: 1, height: 8, borderRadius: 1 }}
                                  />
                                  <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'right' }}>
                                    {formatPercent(row.percentConsumed)}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          <Tooltip
                            title={
                              row.status === 'unknown'
                                ? row.poolModel === 'shared'
                                  ? 'Draws from a shared org pool — no personal cap'
                                  : 'No run allocation configured'
                                : `${formatNum(row.runsRemaining)} of ${formatNum(row.runsAllocated)} runs remaining`
                            }
                          >
                            <Chip
                              size="small"
                              label={meta.label}
                              color={meta.color}
                              variant={meta.filled ? 'filled' : 'outlined'}
                              data-testid={`status-${row.userId}`}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {allRows.length > ROWS_PER_PAGE && (
              <TablePagination
                component="div"
                count={allRows.length}
                page={safePage}
                onPageChange={(_e, p) => setPage(p)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                data-testid="per-user-pagination"
              />
            )}
          </Paper>

          {report.truncated && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="truncation-note">
              Showing the top {(report.users?.length ?? 0).toLocaleString()} users by runs of{' '}
              {(report.total ?? 0).toLocaleString()} total on this stack.
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {report.summary.totalUsers.toLocaleString()} user(s) · generated{' '}
            {new Date(report.generatedAt).toLocaleString()}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default PerUserUsagePage;
